import moment from 'moment';
import 'moment/locale/pl';
import 'moment/locale/ru';
import 'moment/locale/uk';
import 'moment/locale/cs';
import 'moment/locale/de';
import React, { useEffect, useMemo, useRef } from 'react';
import { timestampWithNoMillisecondsToDate } from '../../../../../../base/utils';
import { ResponseItem } from '../../../api/interfaces';
import { ResponseType } from '../../../enums';
import { WorkerData } from '../WorkerData';
import { Specialities } from '../Specialities';
import { Currency } from '../../../../../../base/interfaces/config';
import { VacancyData } from '../VacancyData';
import { ContactData } from '../ContactData';
import { Interactions } from '../Interactions';

import '../styles.scss';
import './styles.scss';
import { useAppDispatch } from '../../../../../../base/hooks';
import { responsesActions } from '../../../redux/slices/responsesSlice';
import { WorkerDescription } from '../WorkerDescription';

interface Props {
    type: ResponseType;
    data: ResponseItem;
    currencies: Currency[];
}

export const TableItem = ({
    type,
    data: { id, workerApplied, employerInvited, worker, isNew, isArchived, vacancy, hireStatus, isQuickResponse },
    currencies,
}: Props) => {
    const dispatch = useAppDispatch();
    const elementRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (type !== ResponseType.Archive && isArchived && elementRef?.current) {
            const archiveNavTab = document.getElementById('responses-navigation-archived');
            if (!archiveNavTab) {
                return;
            }

            const { x: archiveTabX, y: archiveTabY, width: archiveTabWidth } = archiveNavTab.getBoundingClientRect();
            const { x: rowX, y: rowY } = elementRef.current.getBoundingClientRect();
            elementRef.current.style.maxWidth = '1224px';
            elementRef.current.style.transition = 'all 0.25s';
            elementRef.current.style.top = `${rowY}px`;
            elementRef.current.style.left = `${rowX}px`;
            elementRef.current.style.position = 'absolute';
            elementRef.current.style.zIndex = '1';
            setTimeout(() => {
                if (elementRef.current) {
                    elementRef.current.style.top = `${archiveTabY}px`;
                    elementRef.current.style.left = `${
                        archiveTabX - elementRef.current.offsetWidth / 2 + archiveTabWidth / 2
                    }px`;
                    elementRef.current.style.transform = 'scale(0, 0)';
                }
            }, 100);
        }
    }, [type, isArchived, elementRef]);

    const markNewResponseAsReadHandler = () => {
        if (!isNew) {
            return;
        }

        dispatch(responsesActions.markNewResponseAsReadRequest(id));
    };

    const date = useMemo(() => {
        if (type === ResponseType.Applied && workerApplied) {
            return moment(timestampWithNoMillisecondsToDate(workerApplied)).local().format('DD MMM YYYY HH:mm');
        }
        if (type === ResponseType.Invited && employerInvited) {
            return moment(timestampWithNoMillisecondsToDate(employerInvited)).local().format('DD MMM YYYY HH:mm');
        }
        const latestActionDate = Math.max(workerApplied as number, employerInvited as number);
        if (latestActionDate) {
            return moment(timestampWithNoMillisecondsToDate(latestActionDate)).local().format('DD MMM YYYY HH:mm');
        }

        return '';
    }, [workerApplied, employerInvited, type]);

    const descriptionTranslateHandler = (responseId: number) =>
        dispatch(responsesActions.translateWorkerDescription(responseId));

    return (
        <div className={`responses-table-item ${isNew ? 'responses-table-item--new' : ''}`} ref={elementRef}>
            <div className='responses-table__column responses-table__date-column responses-table-item__date'>
                {date}
            </div>
            <div className='responses-table__column responses-table__worker-column'>
                <WorkerData
                    workerData={{ ...worker }}
                    isQuickResponse={isQuickResponse}
                    markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                />
            </div>
            <div className='responses-table__column responses-table__experience-column'>
                {isQuickResponse ? (
                    <WorkerDescription
                        responseId={id}
                        description={worker.description}
                        descriptionTranslated={worker.descriptionTranslated}
                        translateHandler={descriptionTranslateHandler}
                        markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                    />
                ) : (
                    <Specialities
                        specialities={worker.specialities}
                        isUserDeleted={worker.isDeleted}
                        markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                    />
                )}
            </div>
            <div className='responses-table__column responses-table__vacancy-column'>
                <VacancyData
                    currencies={currencies}
                    {...vacancy}
                    markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                />
            </div>
            <div className='responses-table__column responses-table__contacts-column'>
                <ContactData
                    showContacts={worker.showContacts}
                    contacts={worker.contacts}
                    messengerUrl={worker.messengerUrl}
                    isUserDeleted={worker.isDeleted}
                    markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                />
            </div>
            <div className='responses-table__column responses-table__interactions-column responses-table-item__interactions-column'>
                <Interactions
                    showInviteButton={!employerInvited}
                    responseId={id}
                    isResponseArchived={isArchived}
                    hireStatus={hireStatus}
                    vacancyData={vacancy}
                    workerData={worker}
                    isQuickResponse={isQuickResponse}
                    markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                />
            </div>
        </div>
    );
};
